import { useApolloClient, useReactiveVar } from '@apollo/client';
import {
  Button,
  Dropdown,
  FieldInput,
  Headline2,
  Headline3,
  Meta,
  theme,
} from 'aimo-ui';
import {
  CheckIcon,
  DropdownIcon,
} from 'components/account/vehicle/VehicleIcons';
import CopyText from 'components/common/CopyText';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import i18next from 'i18next';
import SafeAreaHeaderContainer from 'navigation/SafeAreaHeaderContainer';
import React, { useState } from 'react';
import { Linking, StyleSheet, Text, View } from 'react-native';
import { useAppConfigStore } from 'stores/appConfigStore';
import {
  country,
  onboardingCompleted,
  tutorialCompletion,
} from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import {
  ScreenContainer,
  ScreenWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { CountryCode, Lang } from 'types/generatedSchemaTypes';
import { MMKVStorage } from 'utils/MMKVStorage';
import { updateStateAndStorage } from 'utils/MMKVStorageUtils';
import { getFlattenObject } from 'utils/commonUtils';
import { isAndroid, isWeb } from 'utils/deviceUtils';
import {
  QA_REFUSAL_NONE_VALUE,
  QA_REFUSAL_OPTIONS,
} from 'utils/qaRefusalOptions';
import { defaultCompletion } from 'utils/tutorialUtils';
import config from '../config/config';
import { useUiStore } from 'stores/uiStore';
import { useQaRefusalStore } from 'stores/qaRefusalStore';

const ListItemsWithoutBackground = () => {
  const flattenedObject = getFlattenObject(config, {});
  const configToList = Object.keys(flattenedObject).map((key: string) => ({
    title: `${key}`,
    subTitle: `${flattenedObject[key]}`,
  }));
  return (
    <View>
      {configToList.map((item: any) => (
        <View key={item.title} style={style.envListItem}>
          <Headline3>{item.title}</Headline3>
          <Meta color={theme.colors.primary}>{item.subTitle}</Meta>
        </View>
      ))}
    </View>
  );
};
const DevScreen: React.FC = () => {
  const { currentStack } = useAfterAuthenticationRedirect();
  const currentCountry = useReactiveVar(country);
  const deviceIdentifier = useAuthStore((state) => state.deviceToken) ?? '';

  const registered = useAuthStore((state) => state.fcmRegistration);
  const hasSentToken = Boolean(registered?.token);
  const accessTok = useAuthStore((state) => state.accessToken) ?? '';
  const [currentLang, setCurrentLang] = useState(i18next.language);
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const [reviewBffNo, setReviewBffNo] = useState('');
  const currentBFF = useAppConfigStore((state) => state.bffUrl);
  const client = useApolloClient();

  const currentQaRefusal = useQaRefusalStore((state) => state.token);
  const qaRefusalItems = QA_REFUSAL_OPTIONS.map((option) => ({
    label: option.label,
    value: option.token ?? QA_REFUSAL_NONE_VALUE,
  }));
  const selectedQaRefusalItem = qaRefusalItems.find(
    (item) => item.value === (currentQaRefusal ?? QA_REFUSAL_NONE_VALUE)
  );

  //cimode is a special code for i18next to show only translation keys during debugging:
  //https://www.i18next.com/overview/api#changelanguage
  const setLanguage = (langCode: Lang | 'cimode') => {
    i18next.changeLanguage(langCode);
    setCurrentLang(langCode);
  };
  const setCountry = (countryCode: CountryCode) => {
    updateStateAndStorage(country, 'country', countryCode);
  };

  const setBffServer = (bffEnv: string, reviewNumber?: any) => {
    let url: string;

    if (bffEnv === 'review' && reviewNumber) {
      url = `https://aimoapp-bff-${reviewNumber}.dev.aimopark.io/graphql`;
    } else {
      url = config.graphqlUrl;
    }

    useAppConfigStore.getState().setBffUrl(url);
  };

  const openPlayStore = (env: 'dev' | 'staging' | 'prod') => {
    const marketAppURL = `market://details?id=com.aimoone.${
      env === 'prod' ? 'app' : env
    }`;
    console.log(
      `Opening PlayStore for ${env} environment with URL: ${marketAppURL}`
    );
    Linking.openURL(marketAppURL);
  };

  return (
    <ScreenContainer>
      <SafeAreaHeaderContainer />
      <ScreenWrapper tabBarHeight={100}>
        <StyledWrapper testID="current-country" maxWidth={300} center>
          <Headline2 center>Set your region</Headline2>
          <Headline3 center>Current: {currentCountry}</Headline3>
          <View style={style.customView}>
            <View style={style.customButtonView}>
              <Button onPress={() => setCountry('FI')} text="Finland" />
            </View>
            <View style={style.customButtonView}>
              <Button onPress={() => setCountry('SE')} text="Sweden" />
            </View>
          </View>
        </StyledWrapper>
        <StyledWrapper testID="current-bff" maxWidth={300} center>
          <Headline2 center>Connect to BFF</Headline2>
          <CopyText copyText={currentBFF}>
            <Meta color={theme.colors.gray300}>{currentBFF}</Meta>
          </CopyText>
          <FieldInput
            value={reviewBffNo}
            onChangeText={(text) => setReviewBffNo(text)}
            keyboardType="numeric"
            title="MR No"
            placeholder="MR Number"
            maxLength={5}
          />
          <Button
            onPress={() => setBffServer('review', reviewBffNo)}
            text="Connect Review BFF"
          />
          <Button onPress={() => setBffServer('dev')} text="Connect DEV BFF" />
        </StyledWrapper>
        <StyledWrapper testID="current-language" maxWidth={300} center>
          <Headline2 center>Set App Language</Headline2>
          <Headline3 center>Current: {currentLang}</Headline3>
          <View style={style.customView}>
            <View style={style.customButtonView}>
              <Button onPress={() => setLanguage('en')} text="EN" />
            </View>
            <View style={style.customButtonView}>
              <Button onPress={() => setLanguage('sv')} text="SV" />
            </View>
            <View style={style.customButtonView}>
              <Button onPress={() => setLanguage('fi')} text="FI" />
            </View>
            <View style={style.customButtonView}>
              <Button onPress={() => setLanguage('no')} text="NO" />
            </View>
          </View>
          <Button
            onPress={() => setLanguage('cimode')}
            text="[Debug] Show translation keys only"
          />
        </StyledWrapper>
        {isAndroid && (
          <StyledWrapper testID="playstore-links" maxWidth={300} center>
            <Headline2 center>Google PlayStore links</Headline2>
            <Headline3 center>Open PlayStore like in update modal</Headline3>
            <Button onPress={() => openPlayStore('dev')} text="DEV" />
            <Button onPress={() => openPlayStore('staging')} text="STAGING" />
            <Button onPress={() => openPlayStore('prod')} text="PRODUCTION" />
          </StyledWrapper>
        )}
        <StyledWrapper testID="tutorial-settings" maxWidth={300} center>
          <Headline2 center>Tutorial settings</Headline2>
          <View style={style.customView}>
            <View style={style.customButtonView}>
              <Button
                text="Reset Tutorials"
                onPress={() => tutorialCompletion(defaultCompletion)}
              />
            </View>
            <View style={style.customButtonView}>
              <Button
                text="Show Tutorials"
                onPress={() =>
                  console.log('completed tutorials', tutorialCompletion())
                }
              />
            </View>
          </View>
        </StyledWrapper>
        <StyledWrapper testID="other-settings" maxWidth={300} center>
          <Headline2 center>Other settings</Headline2>
          <Button
            onPress={() => onboardingCompleted(false)}
            text="Reset onboarding completed"
          />
          <Button
            onPress={() => MMKVStorage.clearAll()}
            text="Clear application cache"
          />
          <Button
            onPress={() => console.log('currentStack', currentStack)}
            text="Show redirect stack"
          />
          <Button
            text={'Print apollo memory internals'}
            onPress={() =>
              console.log(
                JSON.stringify(client?.getMemoryInternals?.(), null, 2)
              )
            }
          />
          {!isWeb && (
            <Button
              onPress={() =>
                MMKVStorage.set(
                  'refreshToken',
                  refreshToken?.substring(0, 1) +
                    'A' +
                    refreshToken?.substring(2)
                )
              }
              text="Force invalid_grant token"
            />
          )}
        </StyledWrapper>
        {/* Temporary section for testing purposes. TODO: Remove when ongoin charging is implemented */}
        <StyledWrapper testID="testing" maxWidth={300} center>
          <Headline2 center>Testing</Headline2>
          <Button
            onPress={() => useUiStore.getState().setSheetModal('stopCharging')}
            text="Stop charging"
          />
        </StyledWrapper>
        <StyledWrapper testID="qa-refusal" maxWidth={300} center>
          <Headline2 center>Adyen card-add refusal</Headline2>
          <Headline3 center>Forces a decline at card-add (TEST only)</Headline3>
          <Dropdown
            testID="qa-refusal-dropdown"
            items={qaRefusalItems}
            defaultItem={selectedQaRefusalItem}
            onSelect={(item) =>
              useQaRefusalStore
                .getState()
                .setToken(
                  item.value === QA_REFUSAL_NONE_VALUE ? null : item.value
                )
            }
            placeholder="Select refusal reason"
            dropdownIcon={DropdownIcon}
            checkIcon={CheckIcon}
          />
        </StyledWrapper>
        <StyledWrapper testID="device-token-status" maxWidth={300} center>
          <Headline2 center>Acceess and device token</Headline2>
          <Headline3>Access token: </Headline3>
          <CopyText copyText={accessTok.toString() || 'undefined'}>
            <Meta testID="access-token" selectable>
              {accessTok.toString().substring(0, 6) + '...'}
            </Meta>
          </CopyText>
          <Headline3>Device token:</Headline3>
          <CopyText copyText={deviceIdentifier || 'undefined'}>
            <Meta testID="device-token" selectable>
              {deviceIdentifier.substring(0, 6) + '...'}
            </Meta>
          </CopyText>
          <Headline3>Device token registered: </Headline3>
          <Text testID="device-token-registered">
            {hasSentToken.toString()}
          </Text>
        </StyledWrapper>
        <StyledWrapper testID="env-vars" maxWidth={400} mb={70}>
          <Headline2 center>Environment vars:</Headline2>
          <ListItemsWithoutBackground />
        </StyledWrapper>
      </ScreenWrapper>
    </ScreenContainer>
  );
};

const style = StyleSheet.create({
  envListItem: {
    margin: 5,
  },
  customView: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  customButtonView: {
    flex: 1,
  },
});
export default DevScreen;
